import { EffectiveCart } from 'interfaces/cart';
import { GiftProductItem, isGiftProductItem } from 'interfaces/line-item';
import { usePointsState } from 'modules/account/pages/bonus-program/state';
import { useMemo } from 'react';

const MINIMUM_ORDER_VALUE = 2000;

interface Input {
  effectiveCart?: EffectiveCart;
}

interface Output {
  availablePoints: number;
  //* check if a user is in the test, if so return 20 euro from the constants */
  minimumOrderValue: number;
  //* Check total sum of cart against minimumOrderValue */
  shouldShowGiftMovMessage: boolean;
  summaryLoading: boolean;
  hasEnoughPoint: boolean;
}

const useBPPhysicalProductRedemption = ({ effectiveCart }: Input): Output => {
  const { activePoints, summaryLoading } = usePointsState();

  const minimumOrderValue = MINIMUM_ORDER_VALUE;

  const redemptionGifts = useMemo(() => {
    return effectiveCart
      ? (effectiveCart.items.filter((item) => isGiftProductItem(item)) as Array<GiftProductItem>)
      : [];
  }, [effectiveCart]);

  const shouldShowGiftMovMessage: boolean = useMemo(() => {
    if (redemptionGifts.length === 0) {
      return false;
    }
    const cartTotal = effectiveCart?.itemsTotal || 0;
    return cartTotal < minimumOrderValue;
  }, [effectiveCart, minimumOrderValue, redemptionGifts]);

  const hasEnoughPoint = useMemo(() => {
    const totalPointsRequired = redemptionGifts
      .filter((item) => item.redeemItem)
      .reduce((sum, item) => {
        return sum + item.points * item.quantity;
      }, 0);

    return totalPointsRequired <= activePoints;
  }, [activePoints, redemptionGifts]);

  const availablePoints = useMemo(() => {
    const pointsInCart = redemptionGifts.reduce((sum, item) => {
      return sum + item.points * item.quantity;
    }, 0);
    return activePoints - pointsInCart;
  }, [activePoints, redemptionGifts]);

  return {
    availablePoints,
    minimumOrderValue,
    shouldShowGiftMovMessage,
    hasEnoughPoint,
    summaryLoading,
  };
};

export default useBPPhysicalProductRedemption;
